import { t } from '@lingui/macro';

export interface BaseRadarSensorsModel {
  radarSensorsType: RadarSensorsType;
  sensorMinRange?: number;
  sensorMaxRange?: number;
  depthDistanceMode?: RadarSensDepthDistanceModeType;
  sensorChamberDepth?: number;
  lostReflection?: number;
  lostReflectionSubstitution?: number;
}

export interface RadarSensSensorModel extends BaseRadarSensorsModel {
  thresholdType?: RadarSensThresholdType;
  thresholdFixedLevel?: number;
  customThreshold?: Array<CustomThresholdRow>;
}
export interface RadarSensLRSensorModel extends BaseRadarSensorsModel {}

export interface CustomThresholdRow {
  index?: number;
  amplitude?: number;
}

export type SiteRadarSensorsModel = RadarSensSensorModel | RadarSensLRSensorModel;
export type SiteRadarSensorFormModel<RSType extends RadarSensorsType> = RSType extends 'RadarSens'
  ? RadarSensSensorModel
  : RSType extends 'UDLive'
    ? RadarSensLRSensorModel
    : never;

const RadarSensors = ['RadarSens', 'UDLive'] as const;
export type RadarSensorsType = (typeof RadarSensors)[number];
export const RadarSensorsOptionsFn = (): Record<RadarSensorsType, string> => ({
  RadarSens: t`RadarSens`,
  UDLive: t`RadarSens+LR`,
});

const RadarSensDepthDistanceMode = {
  Distance: 'Distance',
  Depth: 'Depth',
} as const;
export type RadarSensDepthDistanceModeType =
  (typeof RadarSensDepthDistanceMode)[keyof typeof RadarSensDepthDistanceMode];
export const RadarSensDepthDistanceModeOptionsFn = (): Record<RadarSensDepthDistanceModeType, string> => ({
  [RadarSensDepthDistanceMode.Distance]: t`Distance`,
  [RadarSensDepthDistanceMode.Depth]: t`Depth`,
});

const RadarSensThreshold = {
  FixedLevel: 'FixedLevel',
  Custom: 'Custom',
  None: 'None',
} as const;
export type RadarSensThresholdType = (typeof RadarSensThreshold)[keyof typeof RadarSensThreshold];
export const RadarSensThresholdOptionsFn = (): Record<RadarSensThresholdType, string> => ({
  [RadarSensThreshold.FixedLevel]: t`Fixed Level`,
  [RadarSensThreshold.Custom]: t`Custom`,
  [RadarSensThreshold.None]: t`None`,
});

//

export interface RequestEchoGraphsModel {
  captureTokens: number; // 1 to 10
  captureMode: RequestEchoGraphsCaptureModeType;
  captureThresh?: number; // -32768 to 32767
}

export interface RequestEchoGraphsFormModel extends Omit<RequestEchoGraphsModel, 'captureThresh'> {
  captureThresh?: string; // -32768 to 32767
}

const RequestEchoGraphsCaptureMode = [
  'Any',
  'NegativeResult',
  'ResultAbove',
  'ResultBelow',
  'RateOfChangeGreaterThan',
] as const;
export type RequestEchoGraphsCaptureModeType = (typeof RequestEchoGraphsCaptureMode)[number];
export const RequestEchoGraphsCaptureModeOptionsFn = (): Record<RequestEchoGraphsCaptureModeType, string> => ({
  Any: t`Any`,
  NegativeResult: t`Negative Result`,
  ResultAbove: t`Result above` + ` 'X'`,
  ResultBelow: t`Result below` + ` 'X'`,
  RateOfChangeGreaterThan: t`Rate of change greater than` + ` 'X'`,
});
